/**
 * Helpers to get estimated delivery or pickup time for the current day/time slot.
 * Used to show e.g. "Delivery in 30–45 min" or "Ready for pickup in 15–20 min".
 */

const DAYS_BY_JS: ('SUNDAY' | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY')[] = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

export interface TimeRangesByDay {
  day: string;
  isClosed: boolean;
  ranges: Array<{ openTime: string; closeTime: string; minMinutes: number; maxMinutes: number }>;
}

/**
 * Get current day name (e.g. MONDAY) and time string "HH:mm" in 24h.
 */
function getNowDayAndTime(now: Date): { day: string; time: string } {
  const dayIndex = now.getDay();
  const day = DAYS_BY_JS[dayIndex];
  const h = now.getHours();
  const m = now.getMinutes();
  const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  return { day, time };
}

/**
 * Check if time is within [openTime, closeTime). Assumes 24h "HH:mm".
 */
function isTimeInRange(time: string, openTime: string, closeTime: string): boolean {
  if (time >= openTime && time < closeTime) return true;
  if (closeTime <= openTime) {
    return time >= openTime || time < closeTime;
  }
  return false;
}

/**
 * Returns the estimated min–max minutes for the current day and time from the given ranges.
 * If no matching slot is found, returns null.
 */
export function getEstimatedTimeForNow(
  ranges: TimeRangesByDay[] | undefined | null,
  now: Date = new Date()
): { minMinutes: number; maxMinutes: number } | null {
  if (!ranges?.length) return null;
  const { day, time } = getNowDayAndTime(now);
  const dayEntry = ranges.find((d) => d && typeof d.day === 'string' && d.day === day);
  if (!dayEntry || dayEntry.isClosed || !dayEntry.ranges?.length) return null;
  const slot = dayEntry.ranges.find((r) => r && isTimeInRange(time, r.openTime, r.closeTime));
  if (!slot || typeof slot.minMinutes !== 'number' || typeof slot.maxMinutes !== 'number') return null;
  return { minMinutes: slot.minMinutes, maxMinutes: slot.maxMinutes };
}

/**
 * Format for display: "30–45 min" or "15 min" if min === max.
 */
export function formatEstimatedTimeRange(minMinutes: number, maxMinutes: number): string {
  if (minMinutes === maxMinutes) return `${minMinutes} min`;
  return `${minMinutes}–${maxMinutes} min`;
}
